import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import Loader from "../../Utils/Loader";
import classes from "./styles.module.css";
import { STATUS_COLUMNS, isTransitionAllowed, matchRegistryStatus, formatAmount, formatDate } from "./CrmPayoutBoardUtils";

function KanbanCard(props) {
    var row = props.row;
    var isActive = props.isActive;
    var isSelected = props.isSelected;
    var onSelect = props.onSelect;
    var onCheck = props.onCheck;
    var onCardDragStart = props.onCardDragStart;

    var cardClass = classes.KanbanCard;
    if (isActive) cardClass = cardClass + " " + classes.KanbanCardActive;
    if (isSelected) cardClass = cardClass + " " + classes.KanbanCardSelected;

    var requisites = row.payoutType === "sbp"
        ? [row.sbpPhone, row.sbpBankName].filter(Boolean).join(" · ") || "-"
        : row.cardPanMasked || "-";

    function handleDragStart(e) {
        e.dataTransfer.setData("requestId", String(row.id));
        e.dataTransfer.setData("fromStatus", String(Number(row.status)));
        e.dataTransfer.effectAllowed = "move";
        if (onCardDragStart) onCardDragStart(Number(row.status));
    }

    return (
        <div
            className={cardClass}
            onClick={function () { onSelect(row); }}
            draggable="true"
            onDragStart={handleDragStart}
        >
            <input
                type="checkbox"
                className={classes.CardCheckbox}
                checked={isSelected}
                onClick={function (e) { e.stopPropagation(); }}
                onChange={function (e) { onCheck(row.id, e.target.checked); }}
            />

            <div className={classes.CardName}>{row.fullName || "-"}</div>

            <div className={classes.CardAmount}>{formatAmount(row.amount)}</div>

            <div className={classes.CardMeta}>
                <span className={classes.TypeBadge + " " + classes.TypeCard}>
                    <FormattedMessage
                        id={row.payoutType === "sbp" ? "CrmPayoutBoard.type.sbp" : "CrmPayoutBoard.type.card"}
                        defaultMessage={row.payoutType === "sbp" ? "СБП" : "Карта"}
                    />
                </span>
                <span className={classes.CardRequisites}>{requisites}</span>
            </div>

            {(row.externalReference || row.documentDate) && (
                <div className={classes.CardDoc}>
                    {row.externalReference && <span>{row.externalReference}</span>}
                    {row.externalReference && row.documentDate && <span> </span>}
                    {row.documentDate && <span>{row.documentDate}</span>}
                </div>
            )}
        </div>
    );
}

// Карточка реестра (для реестровых колонок)
function RegistryCard(props) {
    var registry = props.registry;
    var onRegistryClick = props.onRegistryClick;

    var statusLabels = {
        0: { label: "Создан", bg: "#e3f2fd", color: "#1565c0" },
        1: { label: "В обработке", bg: "#fff3e0", color: "#e65100" },
        2: { label: "Обработан", bg: "#e8f5e9", color: "#2e7d32" },
        3: { label: "Ошибки", bg: "#fce4ec", color: "#ad1457" },
        4: { label: "Отложен", bg: "#f5f5f5", color: "#616161" },
    };
    var statusInfo = statusLabels[registry.status] || statusLabels[0];

    return (
        <div
            className={classes.RegistryCard}
            onClick={function () { if (onRegistryClick) onRegistryClick(registry.id); }}
        >
            <div className={classes.RegistryCardName}>{registry.name || "Реестр #" + registry.id}</div>
            <div className={classes.RegistryCardAmount}>{formatAmount(registry.totalAmount)}</div>
            <div className={classes.RegistryCardCount}>{registry.requestCount} заявок</div>
            {registry.date && (
                <div className={classes.RegistryCardDate}>{formatDate(registry.date)}</div>
            )}
            <span
                className={classes.RegistryCardStatus}
                style={{ background: statusInfo.bg, color: statusInfo.color }}
            >
                {statusInfo.label}
            </span>
        </div>
    );
}

function KanbanColumn(props) {
    var column = props.column;
    var items = props.items;
    var registries = props.registries;
    var currentId = props.currentId;
    var selectedIds = props.selectedIds;
    var onSelect = props.onSelect;
    var onCheck = props.onCheck;
    var onCheckAllInColumn = props.onCheckAllInColumn;
    var onRegistryClick = props.onRegistryClick;
    var onDragDrop = props.onDragDrop;
    var onCardDragStart = props.onCardDragStart;
    var draggingFromStatus = props.draggingFromStatus;
    var total = props.total;
    var isColumnLoading = props.isColumnLoading;
    var onLoadMore = props.onLoadMore;

    var _dragOver = useState(false);
    var dragOver = _dragOver[0];
    var setDragOver = _dragOver[1];

    var isValidTarget = draggingFromStatus !== null && isTransitionAllowed(draggingFromStatus, column.status);

    // Реестровая колонка: фильтруем реестры по registryStatus
    var isRegistryColumn = !!column.isRegistry;
    var displayItems;
    if (isRegistryColumn) {
        displayItems = (registries || []).filter(function (reg) {
            return matchRegistryStatus(reg.status, column.registryStatus);
        });
    } else {
        displayItems = items;
    }

    // Сколько всего заявок в колонке на сервере (карточек может быть загружено меньше)
    var columnTotal = isRegistryColumn || total === null || total === undefined
        ? displayItems.length
        : Number(total);
    var hasMore = columnTotal > displayItems.length;

    // Сумма колонки
    var totalAmount = 0;
    displayItems.forEach(function (item) {
        totalAmount += Number(isRegistryColumn ? item.totalAmount : item.amount) || 0;
    });

    // Чекбокс "Выбрать все" (только для не-реестровых колонок)
    var columnItemIds = isRegistryColumn ? [] : displayItems.map(function (r) { return r.id; });
    var allInColumnSelected = !isRegistryColumn && columnItemIds.length > 0 && columnItemIds.every(function (id) {
        return selectedIds && selectedIds.indexOf(id) >= 0;
    });

    function handleCheckAllColumn(e) {
        if (onCheckAllInColumn) {
            onCheckAllInColumn(column.status, e.target.checked, columnItemIds);
        }
    }

    function handleDragOver(e) {
        if (!isValidTarget) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOver(true);
    }

    function handleDragLeave(e) {
        var rect = e.currentTarget.getBoundingClientRect();
        var x = e.clientX;
        var y = e.clientY;
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setDragOver(false);
        }
    }

    function handleDrop(e) {
        e.preventDefault();
        setDragOver(false);

        var requestId = parseInt(e.dataTransfer.getData("requestId"), 10);
        var fromStatus = parseInt(e.dataTransfer.getData("fromStatus"), 10);
        var toStatus = column.status;

        if (isNaN(requestId) || isNaN(fromStatus)) return;
        if (fromStatus === toStatus) return;
        if (!isTransitionAllowed(fromStatus, toStatus)) return;

        if (onDragDrop) {
            onDragDrop(requestId, fromStatus, toStatus);
        }
    }

    var columnBodyClass = classes.ColumnBody;
    if (isValidTarget) {
        columnBodyClass = columnBodyClass + " " + classes.ColumnDropTarget;
    }
    if (dragOver && isValidTarget) {
        columnBodyClass = columnBodyClass + " " + classes.ColumnDragOver;
    }

    return (
        <div className={classes.KanbanColumn}>
            <div className={classes.ColumnHeader} style={{ borderBottomColor: column.color }}>
                <div className={classes.ColumnHeaderTop}>
                    {!isRegistryColumn && columnItemIds.length > 0 && (
                        <input
                            type="checkbox"
                            className={classes.ColumnCheckbox}
                            checked={allInColumnSelected}
                            onChange={handleCheckAllColumn}
                            title="Выбрать все в колонке"
                        />
                    )}
                    <div className={classes.ColumnTitle} style={{ color: column.color }}>
                        {column.label}
                    </div>
                </div>
                <div className={classes.ColumnMeta}>
                    <span className={classes.ColumnCount}>
                        {displayItems.length}
                        {hasMore && (
                            <span className={classes.ColumnTotal}> из {columnTotal}</span>
                        )}
                    </span>
                    {totalAmount > 0 && (
                        <span className={classes.ColumnSum}>{formatAmount(totalAmount)}</span>
                    )}
                </div>
            </div>

            <div
                className={columnBodyClass}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {displayItems.length === 0 && (
                    <div className={classes.EmptyColumn}>
                        {isColumnLoading ? "Загрузка…" : "—"}
                    </div>
                )}
                {isRegistryColumn ? (
                    displayItems.map(function (reg) {
                        return (
                            <RegistryCard
                                key={"reg-" + reg.id}
                                registry={reg}
                                onRegistryClick={onRegistryClick}
                            />
                        );
                    })
                ) : (
                    displayItems.map(function (row) {
                        return (
                            <KanbanCard
                                key={row.id}
                                row={row}
                                isActive={currentId === row.id}
                                isSelected={selectedIds && selectedIds.indexOf(row.id) >= 0}
                                onSelect={onSelect}
                                onCheck={onCheck}
                                onCardDragStart={onCardDragStart}
                            />
                        );
                    })
                )}

                {!isRegistryColumn && hasMore && (
                    <button
                        type="button"
                        className={classes.LoadMoreButton}
                        disabled={!!isColumnLoading}
                        onClick={function () {
                            if (onLoadMore) onLoadMore(column.status);
                        }}
                    >
                        {isColumnLoading
                            ? "Загрузка…"
                            : "Показать ещё (" + (columnTotal - displayItems.length) + ")"}
                    </button>
                )}
            </div>
        </div>
    );
}

function CrmPayoutBoard(props) {
    var data = props.data;
    var registries = props.registries;
    var currentId = props.currentId;
    var selectedIds = props.selectedIds;
    var isLoading = props.isLoading;
    var onSelect = props.onSelect;
    var onCheck = props.onCheck;
    var onCheckAllInColumn = props.onCheckAllInColumn;
    var onRegistryClick = props.onRegistryClick;
    var onDragDrop = props.onDragDrop;
    var visibleStatuses = props.visibleStatuses;
    var totals = props.totals || {};
    var loadingStatuses = props.loadingStatuses || {};
    var onLoadMore = props.onLoadMore;

    var _draggingFrom = useState(null);
    var draggingFromStatus = _draggingFrom[0];
    var setDraggingFromStatus = _draggingFrom[1];

    var rows = Array.isArray(data) ? data : [];

    function handleCardDragStart(status) {
        setDraggingFromStatus(status);
    }

    function handleDragEnd() {
        setDraggingFromStatus(null);
    }

    if (isLoading) {
        return (
            <div className={classes.LoadingWrap}>
                <Loader />
            </div>
        );
    }

    // Группировка заявок по статусу
    var grouped = {};
    STATUS_COLUMNS.forEach(function (col) {
        if (!col.isRegistry) {
            grouped[col.status] = [];
        }
    });
    rows.forEach(function (row) {
        var s = Number(row.status);
        if (grouped[s]) {
            grouped[s].push(row);
        }
    });

    // Фильтрация колонок по visibleStatuses
    var columnsToShow = STATUS_COLUMNS;
    if (visibleStatuses && visibleStatuses.length > 0) {
        columnsToShow = STATUS_COLUMNS.filter(function (col) {
            return visibleStatuses.indexOf(col.status) >= 0;
        });
    }

    return (
        <div className={classes.KanbanBoard} onDragEnd={handleDragEnd}>
            {columnsToShow.map(function (column) {
                return (
                    <KanbanColumn
                        key={column.status}
                        column={column}
                        items={grouped[column.status] || []}
                        registries={registries}
                        currentId={currentId}
                        selectedIds={selectedIds}
                        onSelect={onSelect}
                        onCheck={onCheck}
                        onCheckAllInColumn={onCheckAllInColumn}
                        onRegistryClick={onRegistryClick}
                        onDragDrop={onDragDrop}
                        onCardDragStart={handleCardDragStart}
                        draggingFromStatus={draggingFromStatus}
                        total={totals[column.status]}
                        isColumnLoading={!!loadingStatuses[column.status]}
                        onLoadMore={onLoadMore}
                    />
                );
            })}
        </div>
    );
}

CrmPayoutBoard.defaultProps = {
    data: [],
    registries: [],
    selectedIds: [],
    isLoading: false,
    visibleStatuses: null,
    totals: {},
    loadingStatuses: {},
    onLoadMore: function () {},
    onSelect: function () {},
    onCheck: function () {},
    onCheckAllInColumn: function () {},
    onRegistryClick: function () {},
    onDragDrop: function () {},
};

export default CrmPayoutBoard;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/CrmPayoutBoard/index.js